/**
 * Login + profile menu/dialog + session lifecycle — Phase 3.1.b.
 *
 * Owns:
 *   - the login overlay show/hide and the login form submit handler
 *   - the self-service password reset panels (request a code, redeem it),
 *     including picking a token up out of an emailed `?reset_token=` link
 *   - the session-gate routine (`gate()`) that checks adminAuth, fetches
 *     `/auth/me`, applies role gating, brings up the WS push channel,
 *     and primes the ticket-assignee filters
 *   - the profile menu (compact dropdown next to the topbar) and the
 *     profile/password-change dialog + form submit handler
 *   - logout (with the customer-care shift guard)
 *   - the small `valid/invalid` session-status badge
 *
 * State injection: admin.js calls `configureAuth({...})` once at boot
 * with thunks for `ADMIN_ME` (read + write), the role-gating callback,
 * the ticket-assignee filter helpers, the view-router, and a few
 * constants/DOM-getters. Globals (`adminAuth`, `adminAPI`,
 * `adminNotifications`, `window.adminViews.*`) are read directly
 * since they're already module-shared singletons.
 *
 * `installAuthListeners()` wires the DOM event listeners after the
 * config + DOM are ready — admin.js calls it once.
 */

import { hasRole } from './roles.js';
import * as adminAPI from '../../js/api-client.js';
import * as adminAuth from '../../js/auth-session.js';
import * as adminNotifications from '../../js/notifications.js';

// ----- Configuration --------------------------------------------------

const cfg = {
  getAdminMe: () => ({ roles: [] }),
  setAdminMe: () => {},
  applyRoleGating: () => {},
  switchView: () => {},
  getViewElements: () => ({}),
  getAnalyticsIframe: () => null,
  populateTicketAssigneeUserFilter: () => {},
  applyDefaultAssigneeUserFilter: () => {},
  ensureTechnicianUsersLoaded: () => Promise.resolve(),
  ticketAssigneeUserManualGet: () => false,
  openDialog: null,
  sessionTtlMs: 24 * 60 * 60 * 1000,
  apiRoutes: {},
};

export function configureAuth(options) {
  if (!options || typeof options !== 'object') return;
  Object.assign(cfg, options);
}

// ----- DOM lookups (resolved lazily) ----------------------------------

function $(id) { return document.getElementById(id); }

const dom = {
  loginOverlay: () => $('loginOverlay'),
  loginForm: () => $('loginForm'),
  loginSubmitBtn: () => $('loginSubmitBtn'),
  sessionStatus: () => $('sessionStatus'),
  logoutBtn: () => $('logoutBtn'),
  profileMenu: () => $('profileMenu'),
  profileMenuToggle: () => $('profileMenuToggle'),
  profileMenuLabel: () => $('profileMenuLabel'),
  profileMenuProfileBtn: () => $('profileMenuProfile'),
  profileMenuHelp: () => $('profileMenuHelp'),
  profileDialog: () => $('profileDlg'),
  profileForm: () => $('profileForm'),
  profileFormError: () => $('profileFormError'),
  profileCurrentPasswordInput: () => $('profileCurrentPassword'),
  profileNewPasswordInput: () => $('profileNewPassword'),
  profileConfirmPasswordInput: () => $('profileConfirmPassword'),
  profilePasswordSubmit: () => $('profilePasswordSubmit'),
  // Login overlay panels (credentials → second factor → forced change →
  // enrolment), plus the self-service reset pair hanging off "Forgot password?"
  loginStepCredentials: () => $('loginStepCredentials'),
  loginStepMfa: () => $('loginStepMfa'),
  loginStepPasswordChange: () => $('loginStepPasswordChange'),
  loginStepMfaEnrol: () => $('loginStepMfaEnrol'),
  loginStepForgot: () => $('loginStepForgot'),
  loginStepReset: () => $('loginStepReset'),
  loginError: () => $('loginError'),
  loginMfaForm: () => $('loginMfaForm'),
  loginMfaCode: () => $('loginMfaCode'),
  loginMfaError: () => $('loginMfaError'),
  loginMfaCancel: () => $('loginMfaCancel'),
  loginChangeForm: () => $('loginChangeForm'),
  loginChangeReason: () => $('loginChangeReason'),
  loginChangeCurrent: () => $('loginChangeCurrent'),
  loginChangeNew: () => $('loginChangeNew'),
  loginChangeConfirm: () => $('loginChangeConfirm'),
  loginChangeHint: () => $('loginChangeHint'),
  loginChangeError: () => $('loginChangeError'),
  loginEnrolForm: () => $('loginEnrolForm'),
  loginEnrolQr: () => $('loginEnrolQr'),
  loginEnrolSecret: () => $('loginEnrolSecret'),
  loginEnrolUri: () => $('loginEnrolUri'),
  loginEnrolCopy: () => $('loginEnrolCopy'),
  loginEnrolCode: () => $('loginEnrolCode'),
  loginEnrolError: () => $('loginEnrolError'),
  loginEnrolCodes: () => $('loginEnrolCodes'),
  loginEnrolCodesList: () => $('loginEnrolCodesList'),
  loginEnrolDone: () => $('loginEnrolDone'),
  loginForgotLink: () => $('loginForgotLink'),
  loginForgotForm: () => $('loginForgotForm'),
  loginForgotEmail: () => $('loginForgotEmail'),
  loginForgotError: () => $('loginForgotError'),
  loginForgotCancel: () => $('loginForgotCancel'),
  loginForgotSubmit: () => $('loginForgotSubmit'),
  loginResetForm: () => $('loginResetForm'),
  loginResetNotice: () => $('loginResetNotice'),
  loginResetToken: () => $('loginResetToken'),
  loginResetNew: () => $('loginResetNew'),
  loginResetConfirm: () => $('loginResetConfirm'),
  loginResetMfaField: () => $('loginResetMfaField'),
  loginResetMfaCode: () => $('loginResetMfaCode'),
  loginResetHint: () => $('loginResetHint'),
  loginResetError: () => $('loginResetError'),
  loginResetCancel: () => $('loginResetCancel'),
  loginResetSubmit: () => $('loginResetSubmit'),
  // Profile security panel
  profilePasswordAge: () => $('profilePasswordAge'),
  profilePasswordRules: () => $('profilePasswordRules'),
  profileMfaStatus: () => $('profileMfaStatus'),
  profileMfaEnrolBtn: () => $('profileMfaEnrolBtn'),
  profileMfaCodesBtn: () => $('profileMfaCodesBtn'),
  profileMfaDisableBtn: () => $('profileMfaDisableBtn'),
  profileMfaEnrolBox: () => $('profileMfaEnrolBox'),
  profileMfaQr: () => $('profileMfaQr'),
  profileMfaSecret: () => $('profileMfaSecret'),
  profileMfaUri: () => $('profileMfaUri'),
  profileMfaCode: () => $('profileMfaCode'),
  profileMfaEnrolCancel: () => $('profileMfaEnrolCancel'),
  profileMfaEnrolConfirm: () => $('profileMfaEnrolConfirm'),
  profileMfaCodesList: () => $('profileMfaCodesList'),
  profileSessionsList: () => $('profileSessionsList'),
  profileSessionsRevokeBtn: () => $('profileSessionsRevokeBtn'),
  profileSignInActivity: () => $('profileSignInActivity'),
  passwordExpiryBanner: () => $('passwordExpiryBanner'),
  passwordExpiryText: () => $('passwordExpiryText'),
  passwordExpiryAction: () => $('passwordExpiryAction'),
  passwordExpiryDismiss: () => $('passwordExpiryDismiss'),
};

// ----- Sign-in sequence state -----------------------------------------
//
// The server decides what a sign-in still needs and says so in its answer: a
// 401 with `mfa_required`, or a 200 carrying a *scoped* token plus
// `password_change_required` / `mfa_enrolment_required`. This state is what
// carries the user's credentials across those steps — the forced-change screen
// needs the password they just typed as its "current password", and a
// two-factor retry needs to re-POST the same credentials with the code.
//
// It is cleared the moment the sequence finishes or is abandoned.
const loginFlow = {
  email: '',
  password: '',
  step: 'credentials',
  enrolSecret: '',
};

function clearLoginFlow() {
  loginFlow.email = '';
  loginFlow.password = '';
  loginFlow.step = 'credentials';
  loginFlow.enrolSecret = '';
}

const LOGIN_STEPS = {
  credentials: dom.loginStepCredentials,
  mfa: dom.loginStepMfa,
  passwordChange: dom.loginStepPasswordChange,
  mfaEnrol: dom.loginStepMfaEnrol,
  forgot: dom.loginStepForgot,
  reset: dom.loginStepReset,
};

function showLoginStep(name) {
  loginFlow.step = name;
  Object.entries(LOGIN_STEPS).forEach(([key, getter]) => {
    const el = getter();
    if (el) el.hidden = key !== name;
  });
  const focusMap = {
    mfa: dom.loginMfaCode,
    passwordChange: dom.loginChangeNew,
    mfaEnrol: dom.loginEnrolCode,
    forgot: dom.loginForgotEmail,
    // The token arrives prefilled from an emailed link, so the first thing
    // left to do is choose the password.
    reset: dom.loginResetNew,
  };
  const target = focusMap[name] && focusMap[name]();
  if (target) setTimeout(() => { try { target.focus(); } catch {} }, 60);
}

/**
 * Show the enrolment QR, or leave the image hidden if the server couldn't
 * render one.
 *
 * `qr_data_uri` is an inline SVG data URI generated server-side — the seed is
 * never handed to a third-party QR service. When it is missing the hand-entry
 * key below it still works, so a failed render costs convenience and nothing
 * more; that is why there is no error path here.
 */
function showEnrolQr(img, dataUri) {
  if (!img) return;
  if (dataUri) {
    img.setAttribute('src', dataUri);
    img.hidden = false;
  } else {
    img.removeAttribute('src');
    img.hidden = true;
  }
}

function setFieldError(getter, message) {
  const el = getter();
  if (!el) return;
  el.textContent = message || '';
  el.style.display = message ? '' : 'none';
}

function errorText(err, fallback) {
  const body = err && err.body;
  const detail = body && body.detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return (err && err.message) || fallback;
}

function errorCode(err) {
  const body = err && err.body;
  const detail = body && body.detail;
  if (detail && typeof detail === 'object' && detail.code) return detail.code;
  return null;
}

// ----- Session-status badge -------------------------------------------

export function updateSessionStatus() {
  const el = dom.sessionStatus();
  if (!el) return;
  const valid = !!(adminAuth && adminAuth.isSessionValid && adminAuth.isSessionValid());
  el.textContent = valid ? 'valid' : 'invalid';
}

// ----- Profile menu ---------------------------------------------------

export function closeProfileMenu() {
  const menu = dom.profileMenu();
  if (menu && menu.open) menu.open = false;
}

export function updateProfileMenuLabel() {
  const label = dom.profileMenuLabel();
  if (!label) return;
  const me = cfg.getAdminMe() || {};
  const name = (typeof me.username === 'string' && me.username.trim())
    ? me.username.trim()
    : (typeof me.email === 'string' && me.email.trim())
      ? me.email.trim()
      : null;
  label.textContent = name || 'Profile';
}

// ----- Profile / password-change dialog -------------------------------

function ensureProfileDialogClosed() {
  const dlg = dom.profileDialog();
  if (!dlg) return;
  try { dlg.close(); }
  catch { dlg.open = false; }
}

function clearProfileFormError() {
  const errorEl = dom.profileFormError();
  if (!errorEl) return;
  errorEl.textContent = '';
  errorEl.style.display = 'none';
}

function showProfileFormError(message) {
  const errorEl = dom.profileFormError();
  if (!errorEl) return;
  if (message) {
    errorEl.textContent = message;
    errorEl.style.display = '';
  } else {
    errorEl.textContent = '';
    errorEl.style.display = 'none';
  }
}

/**
 * Open the password-change dialog. Caller must have already injected
 * `openDialog` into the surrounding scope; we use the global modal
 * helper that lives on `window.adminViews.__openDialog` (set by admin.js
 * via configureAuth). Since admin.js's `openDialog` is local, we accept
 * it as a config option and fall back to `dialog.showModal()`.
 */
export function openProfileDialog() {
  const dlg = dom.profileDialog();
  if (!dlg) return;
  const form = dom.profileForm();
  if (form) form.reset();
  clearProfileFormError();
  closeProfileMenu();
  if (typeof cfg.openDialog === 'function') {
    cfg.openDialog(dlg);
  } else {
    try {
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.open = true;
    } catch {
      dlg.open = true;
    }
  }
  const currentInput = dom.profileCurrentPasswordInput();
  if (currentInput) {
    setTimeout(() => { try { currentInput.focus(); } catch {} }, 60);
  }
  renderPasswordPolicyNotes();
  loadProfileSecurity();
}

// ----- Profile security panel -----------------------------------------
//
// Second factor, live sessions and recent sign-ins, all self-service. The last
// one matters more than it looks: account takeover is noticed by the person who
// knows they were asleep at 03:00, not by whoever reviews the log.

function renderPasswordPolicyNotes() {
  const me = cfg.getAdminMe() || {};
  const ageEl = dom.profilePasswordAge();
  if (ageEl) {
    const days = me.password_expires_in_days;
    if (days == null) ageEl.textContent = '';
    else if (days < 0) ageEl.textContent = 'Your password has expired and must be changed now.';
    else ageEl.textContent = `Your password expires in ${days} day${days === 1 ? '' : 's'}.`;
  }
  const rules = dom.profilePasswordRules();
  if (rules) {
    const min = me.password_min_length || 12;
    rules.textContent = `At least ${min} characters, not a password you have used here before, and not `
      + 'built from your name or email. Three or four unrelated words work well and are easier to remember '
      + 'than a mangled single word.';
  }
}

async function loadProfileSecurity() {
  const statusEl = dom.profileMfaStatus();
  const me = cfg.getAdminMe() || {};
  if (statusEl) {
    if (me.mfa_enrolled) {
      statusEl.textContent = 'On — an authenticator code is required at every sign-in.';
    } else if (me.mfa_required) {
      statusEl.textContent = 'Required for your role but not set up yet.';
    } else {
      statusEl.textContent = 'Off. Strongly recommended: it is the one control that still protects '
        + 'the account if your password is ever stolen.';
    }
  }
  const enrolBtn = dom.profileMfaEnrolBtn();
  const codesBtn = dom.profileMfaCodesBtn();
  const disableBtn = dom.profileMfaDisableBtn();
  if (enrolBtn) enrolBtn.hidden = Boolean(me.mfa_enrolled);
  if (codesBtn) codesBtn.hidden = !me.mfa_enrolled;
  // Mandatory for the role → not a user preference, so no off switch.
  if (disableBtn) disableBtn.hidden = !me.mfa_enrolled || Boolean(me.mfa_required);
  const box = dom.profileMfaEnrolBox();
  if (box) box.hidden = true;
  const codesList = dom.profileMfaCodesList();
  if (codesList) codesList.hidden = true;

  await Promise.all([loadProfileSessions(), loadProfileActivity()]);
}

function describeDevice(ua) {
  if (!ua) return 'unknown device';
  const browser = /(Chrome|Firefox|Safari|Edg|OkHttp)/i.exec(ua);
  const platform = /(Windows|Android|iPhone|iPad|Mac OS X|Linux)/i.exec(ua);
  return [browser ? browser[1] : null, platform ? platform[1] : null].filter(Boolean).join(' · ')
    || ua.slice(0, 40);
}

async function loadProfileSessions() {
  const el = dom.profileSessionsList();
  if (!el) return;
  try {
    const rows = await adminAPI.fetch(cfg.apiRoutes.mySessions, { method: 'GET' });
    if (!rows.length) { el.textContent = 'No other sessions.'; return; }
    el.innerHTML = rows.map((r) => {
      const where = [r.ip || 'unknown address', describeDevice(r.user_agent)].join(' · ');
      const app = r.population === 'admin' ? 'Admin console'
        : r.population === 'tech' ? 'Technician app'
          : r.population === 'infra_tech' ? 'Infra-tech app' : r.population;
      return `<div>• <strong>${escapeText(app)}</strong> — ${escapeText(where)}</div>`;
    }).join('');
  } catch {
    el.textContent = 'Could not load your sessions.';
  }
}

async function loadProfileActivity() {
  const el = dom.profileSignInActivity();
  if (!el) return;
  try {
    const rows = await adminAPI.fetch(cfg.apiRoutes.mySignInActivity(10), { method: 'GET' });
    if (!rows.length) { el.textContent = 'No recorded sign-ins yet.'; return; }
    el.innerHTML = rows.map((r) => {
      const when = r.occurred_at ? new Date(r.occurred_at).toLocaleString() : '—';
      const ok = r.outcome === 'success';
      const label = ok ? 'signed in' : (r.outcome || '').replace(/_/g, ' ');
      return `<div>• ${escapeText(when)} — ${ok ? '' : '<strong>'}${escapeText(label)}${ok ? '' : '</strong>'}`
        + ` from ${escapeText(r.ip || 'unknown address')}</div>`;
    }).join('');
  } catch {
    el.textContent = 'Could not load your recent sign-ins.';
  }
}

function escapeText(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

async function startProfileEnrolment() {
  const box = dom.profileMfaEnrolBox();
  try {
    const data = await adminAPI.fetch(cfg.apiRoutes.mfaBegin, { method: 'POST' });
    loginFlow.enrolSecret = data.secret || '';
    showEnrolQr(dom.profileMfaQr(), data.qr_data_uri);
    const secretEl = dom.profileMfaSecret();
    if (secretEl) secretEl.textContent = data.secret_grouped || data.secret || '';
    const uriEl = dom.profileMfaUri();
    if (uriEl) uriEl.setAttribute('href', data.otpauth_uri || '#');
    if (box) box.hidden = false;
  } catch (err) {
    adminNotifications.showFlash(errorText(err, 'Could not start two-factor setup.'), 'error');
  }
}

async function confirmProfileEnrolment() {
  const input = dom.profileMfaCode();
  const code = input ? input.value.trim() : '';
  if (!code) {
    adminNotifications.showFlash('Enter the code from your authenticator app.', 'error');
    return;
  }
  try {
    const data = await adminAPI.fetch(cfg.apiRoutes.mfaConfirm, {
      method: 'POST',
      body: JSON.stringify({ secret: loginFlow.enrolSecret, code }),
    });
    if (input) input.value = '';
    const box = dom.profileMfaEnrolBox();
    if (box) box.hidden = true;
    const me = cfg.getAdminMe() || {};
    cfg.setAdminMe(Object.assign({}, me, { mfa_enrolled: true }));
    adminNotifications.showFlash('Two-factor is on');
    // Refresh the panel first (it resets the enrolment widgets), then show the
    // codes — they are displayed exactly once and must survive that reset.
    await loadProfileSecurity();
    showRecoveryCodes(data.recovery_codes || []);
  } catch (err) {
    adminNotifications.showFlash(errorText(err, 'That code was not accepted.'), 'error');
  }
}

function showRecoveryCodes(codes) {
  const el = dom.profileMfaCodesList();
  if (!el || !codes.length) return;
  el.textContent = `Recovery codes — each works once, shown only now:\n\n${codes.join('\n')}`;
  el.hidden = false;
}

async function regenerateRecoveryCodes() {
  const current = window.prompt('Confirm your current password to issue new recovery codes:');
  if (!current) return;
  try {
    const data = await adminAPI.fetch(cfg.apiRoutes.mfaRecoveryCodes, {
      method: 'POST',
      body: JSON.stringify({ current_password: current }),
    });
    showRecoveryCodes(data.recovery_codes || []);
    adminNotifications.showFlash('New recovery codes issued — the old ones no longer work');
  } catch (err) {
    adminNotifications.showFlash(errorText(err, 'Could not issue new codes.'), 'error');
  }
}

async function disableMfa() {
  const current = window.prompt('Confirm your current password to turn off two-factor:');
  if (!current) return;
  const code = window.prompt('Enter a current authenticator code (or a recovery code):');
  if (!code) return;
  try {
    await adminAPI.fetch(cfg.apiRoutes.mfaDisable, {
      method: 'POST',
      body: JSON.stringify({ current_password: current, code }),
    });
    const me = cfg.getAdminMe() || {};
    cfg.setAdminMe(Object.assign({}, me, { mfa_enrolled: false }));
    adminNotifications.showFlash('Two-factor turned off');
    await loadProfileSecurity();
  } catch (err) {
    adminNotifications.showFlash(errorText(err, 'Could not turn off two-factor.'), 'error');
  }
}

async function revokeOtherSessions() {
  try {
    const res = await adminAPI.fetch(cfg.apiRoutes.mySessionsRevokeAll, { method: 'POST' });
    adminNotifications.showFlash(`Ended ${res.count} other session(s)`);
    await loadProfileSessions();
  } catch (err) {
    adminNotifications.showFlash(errorText(err, 'Could not end your other sessions.'), 'error');
  }
}

async function handleProfileFormSubmit(event) {
  event.preventDefault();
  const currentInput = dom.profileCurrentPasswordInput();
  const newInput = dom.profileNewPasswordInput();
  const confirmInput = dom.profileConfirmPasswordInput();
  const submitBtn = dom.profilePasswordSubmit();

  const current = currentInput ? currentInput.value : '';
  const next = newInput ? newInput.value : '';
  const confirm = confirmInput ? confirmInput.value : '';

  showProfileFormError('');
  if (!current || !next || !confirm) {
    showProfileFormError('All fields are required.');
    return;
  }
  const minLength = (cfg.getAdminMe() || {}).password_min_length || 12;
  if (next.length < minLength) {
    showProfileFormError(`New password must be at least ${minLength} characters long.`);
    return;
  }
  if (next !== confirm) {
    showProfileFormError('New passwords do not match.');
    return;
  }
  if (!submitBtn) return;
  const defaultLabel = submitBtn.textContent;
  submitBtn.disabled = true;
  submitBtn.textContent = 'Saving…';
  try {
    const result = await adminAPI.fetch(cfg.apiRoutes.changePassword || '/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: current, new_password: next }),
    });
    showProfileFormError('');
    // A change revokes every session for the account, including this one, and
    // hands back a replacement — so re-anchor the local expiry instead of
    // letting the next request 401 and bounce the user to the login overlay.
    const ttl = (result.expires_in || 0) * 1000;
    adminAuth.setSessionExpiry(ttl > 0 ? ttl : cfg.sessionTtlMs);
    hidePasswordExpiryBanner();
    const revoked = Number(result.sessions_revoked || 0);
    adminNotifications.showFlash(
      revoked > 1
        ? `Password updated — ${revoked - 1} other session(s) signed out`
        : 'Password updated',
    );
    const form = dom.profileForm();
    if (form) form.reset();
    ensureProfileDialogClosed();
  } catch (err) {
    const detail = err?.detail || err?.message || 'Failed to update password.';
    showProfileFormError(detail);
  } finally {
    submitBtn.disabled = false;
    submitBtn.textContent = defaultLabel;
  }
}

// ----- Session gate (login overlay → /auth/me → role gating) ----------

export async function gate() {
  const overlay = dom.loginOverlay();
  if (!adminAuth.isSessionValid()) {
    if (overlay) {
      overlay.hidden = false;
      overlay.style.display = '';
    }
    return false;
  }
  // Optional server validation
  try { await adminAPI.fetch(cfg.apiRoutes.validate, { method: 'GET' }); }
  catch {
    if (overlay) {
      overlay.hidden = false;
      overlay.style.display = 'grid';
    }
    return false;
  }
  // Fetch roles for client-side gating
  try {
    const me = await adminAPI.fetch('/auth/me', { method: 'GET' });
    cfg.setAdminMe(me);
    // Shown on every boot inside the warning window, not just right after a
    // sign-in — a reload should not make the reminder disappear.
    if (me.password_expiry_warning != null) showPasswordExpiryBanner(me.password_expiry_warning);
    else hidePasswordExpiryBanner();
  } catch {
    cfg.setAdminMe({ roles: [] });
  }
  updateProfileMenuLabel();
  cfg.applyRoleGating();
  // Reset shift prompt so it shows for the new session
  const sm = window.adminViews && window.adminViews.shiftManagement;
  if (sm && typeof sm.setPromptDismissed === 'function') sm.setPromptDismissed(false);
  try { adminNotifications.ensureSocket(); } catch {}
  if (sm) {
    if (typeof sm.ensurePrompt === 'function') sm.ensurePrompt().catch(() => {});
    if (typeof sm.startPolling === 'function') sm.startPolling();
  }
  cfg.populateTicketAssigneeUserFilter();
  cfg.applyDefaultAssigneeUserFilter();
  cfg.ensureTechnicianUsersLoaded().then(() => {
    cfg.populateTicketAssigneeUserFilter();
    if (!cfg.ticketAssigneeUserManualGet()) {
      cfg.applyDefaultAssigneeUserFilter();
    }
  }).catch(() => {});
  if (overlay) {
    overlay.hidden = true;
    overlay.style.display = 'none';
  }
  return true;
}

// ----- Login form submission ------------------------------------------

export async function handleLoginSubmit(event) {
  if (event) event.preventDefault();
  const form = dom.loginForm();
  if (!form) return false;
  const formData = new FormData(form);
  const email = String(formData.get('email') || '').trim();
  const password = String(formData.get('password') || '');
  if (!email || !password) {
    setFieldError(dom.loginError, 'Email and password are required.');
    return false;
  }
  loginFlow.email = email;
  loginFlow.password = password;
  setFieldError(dom.loginError, '');
  await attemptLogin();
  return false;
}

/**
 * POST the credentials (plus a second-factor code, once we have one) and act
 * on whichever of the four possible answers comes back:
 *
 *   401 mfa_required          → show the code panel
 *   200 + mfa_enrolment_required → privileged role with no second factor yet
 *   200 + password_change_required → expired, or reset by an admin
 *   200                       → a real session
 *
 * `skipUnauthorizedHandler` matters here: a 401 asking for a code is an
 * expected answer, and letting the global handler treat it as a dead session
 * would clear the scoped token the server just issued.
 */
async function attemptLogin(mfaCode) {
  const body = { email: loginFlow.email, password: loginFlow.password };
  if (mfaCode) body.mfa_code = mfaCode;
  const errorTarget = mfaCode ? dom.loginMfaError : dom.loginError;
  try {
    const data = await adminAPI.fetch(cfg.apiRoutes.login, {
      method: 'POST',
      body: JSON.stringify(body),
      skipUnauthorizedHandler: true,
    });
    if (data.mfa_enrolment_required) {
      await beginMandatoryEnrolment();
      return;
    }
    if (data.password_change_required) {
      const reason = dom.loginChangeReason();
      if (reason) {
        reason.textContent = 'Your password has expired or was reset, so it must be changed before '
          + 'you can continue. Passwords are rotated monthly and cannot repeat a recent one.';
      }
      const current = dom.loginChangeCurrent();
      if (current) current.value = loginFlow.password;
      setFieldError(dom.loginChangeError, '');
      showLoginStep('passwordChange');
      return;
    }
    finishLogin(data);
  } catch (err) {
    const code = errorCode(err);
    if (code === 'mfa_required') {
      setFieldError(dom.loginMfaError, '');
      showLoginStep('mfa');
      return;
    }
    if (code === 'mfa_invalid') {
      setFieldError(dom.loginMfaError, errorText(err, 'That code was not accepted.'));
      showLoginStep('mfa');
      return;
    }
    // Lockout, blocked address, deactivated account: the server's message is
    // written for the person reading it, so show it rather than a generic one.
    setFieldError(errorTarget, errorText(err, 'Sign-in failed.'));
    if (mfaCode) showLoginStep('mfa');
  }
}

function finishLogin(data) {
  const ttl = (data.expires_in_seconds || data.expires_in || 0) * 1000;
  adminAuth.setSessionExpiry(ttl > 0 ? ttl : cfg.sessionTtlMs);
  const overlay = dom.loginOverlay();
  if (overlay) {
    overlay.hidden = true;
    overlay.style.display = 'none';
  }
  const loginForm = dom.loginForm();
  if (loginForm) loginForm.reset();
  clearLoginFlow();
  showLoginStep('credentials');
  adminNotifications.showFlash('Signed in');
  gate().then(() => {
    cfg.switchView('analytics');
    if (data.password_expires_in_days != null) {
      showPasswordExpiryBanner(data.password_expires_in_days);
    }
  }).catch(() => {});
}

async function handleLoginMfaSubmit(event) {
  if (event) event.preventDefault();
  const input = dom.loginMfaCode();
  const code = input ? input.value.trim() : '';
  if (!code) {
    setFieldError(dom.loginMfaError, 'Enter the code from your authenticator app.');
    return;
  }
  await attemptLogin(code);
  if (input && loginFlow.step === 'mfa') input.value = '';
}

async function handleForcedChangeSubmit(event) {
  if (event) event.preventDefault();
  const current = dom.loginChangeCurrent();
  const next = dom.loginChangeNew();
  const confirm = dom.loginChangeConfirm();
  const currentValue = current ? current.value : '';
  const nextValue = next ? next.value : '';
  if (!currentValue || !nextValue) {
    setFieldError(dom.loginChangeError, 'Fill in both passwords.');
    return;
  }
  if (nextValue !== (confirm ? confirm.value : '')) {
    setFieldError(dom.loginChangeError, 'The new passwords do not match.');
    return;
  }
  setFieldError(dom.loginChangeError, '');
  try {
    // The scoped token rides in the cookie the login response set, so this
    // call needs no special handling — and it works on nothing else.
    const data = await adminAPI.fetch(cfg.apiRoutes.changePassword, {
      method: 'POST',
      body: JSON.stringify({ current_password: currentValue, new_password: nextValue }),
      skipUnauthorizedHandler: true,
    });
    if (next) next.value = '';
    if (confirm) confirm.value = '';
    adminNotifications.showFlash('Password updated');
    finishLogin(data);
  } catch (err) {
    setFieldError(dom.loginChangeError, errorText(err, 'Could not set that password.'));
  }
}

async function beginMandatoryEnrolment() {
  try {
    const data = await adminAPI.fetch(cfg.apiRoutes.mfaBegin, {
      method: 'POST',
      skipUnauthorizedHandler: true,
    });
    loginFlow.enrolSecret = data.secret || '';
    showEnrolQr(dom.loginEnrolQr(), data.qr_data_uri);
    const secretEl = dom.loginEnrolSecret();
    if (secretEl) secretEl.textContent = data.secret_grouped || data.secret || '';
    const uriEl = dom.loginEnrolUri();
    if (uriEl) uriEl.setAttribute('href', data.otpauth_uri || '#');
    setFieldError(dom.loginEnrolError, '');
    const codesBox = dom.loginEnrolCodes();
    if (codesBox) codesBox.hidden = true;
    const enrolForm = dom.loginEnrolForm();
    if (enrolForm) enrolForm.hidden = false;
    showLoginStep('mfaEnrol');
  } catch (err) {
    setFieldError(dom.loginError, errorText(err, 'Could not start two-factor setup.'));
    showLoginStep('credentials');
  }
}

async function handleEnrolSubmit(event) {
  if (event) event.preventDefault();
  const input = dom.loginEnrolCode();
  const code = input ? input.value.trim() : '';
  if (!code) {
    setFieldError(dom.loginEnrolError, 'Enter the code shown in your authenticator app.');
    return;
  }
  try {
    const data = await adminAPI.fetch(cfg.apiRoutes.mfaConfirm, {
      method: 'POST',
      body: JSON.stringify({ secret: loginFlow.enrolSecret, code }),
      skipUnauthorizedHandler: true,
    });
    const list = dom.loginEnrolCodesList();
    if (list) list.textContent = (data.recovery_codes || []).join('\n');
    const box = dom.loginEnrolCodes();
    if (box) box.hidden = false;
    const enrolForm = dom.loginEnrolForm();
    if (enrolForm) enrolForm.hidden = true;
    // Hold the session until they confirm the codes are saved — this is the
    // only time the codes are ever shown.
    const done = dom.loginEnrolDone();
    if (done) done.onclick = () => finishLogin(data);
  } catch (err) {
    setFieldError(dom.loginEnrolError, errorText(err, 'That code was not accepted.'));
  }
}

// ----- Self-service password reset ------------------------------------
//
// Two more panels in the same sequence: ask for a code, then redeem it. They
// are part of the login overlay rather than a page of their own because every
// route into them starts at "I cannot sign in" — including the emailed link,
// which lands on the console with `?reset_token=` and is picked up by
// `consumeResetTokenFromUrl()` below.
//
// The request step deliberately gives the same confirmation whatever the
// server did, because the server deliberately gives the same answer: the
// address may be unknown, deactivated, or inside the resend cooldown, and
// the SPA is not told which.

function openForgotPanel(prefillEmail) {
  const input = dom.loginForgotEmail();
  if (input) {
    const typed = (() => {
      const form = dom.loginForm();
      if (!form) return '';
      return String(new FormData(form).get('email') || '').trim();
    })();
    input.value = prefillEmail || typed || input.value || '';
  }
  setFieldError(dom.loginForgotError, '');
  showLoginStep('forgot');
}

function openResetPanel(token, notice) {
  const tokenInput = dom.loginResetToken();
  if (tokenInput && token) tokenInput.value = token;
  const noticeEl = dom.loginResetNotice();
  if (noticeEl) {
    noticeEl.textContent = notice
      || 'Paste the code from the email, then choose a new password. The code works once.';
  }
  const mfaField = dom.loginResetMfaField();
  if (mfaField) mfaField.hidden = true;
  const mfaCode = dom.loginResetMfaCode();
  if (mfaCode) mfaCode.value = '';
  setFieldError(dom.loginResetError, '');
  showLoginStep('reset');
}

function backToCredentials() {
  clearLoginFlow();
  setFieldError(dom.loginForgotError, '');
  setFieldError(dom.loginResetError, '');
  const token = dom.loginResetToken();
  if (token) token.value = '';
  ['loginResetNew', 'loginResetConfirm', 'loginResetMfaCode'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.value = '';
  });
  showLoginStep('credentials');
}

async function handleForgotSubmit(event) {
  if (event) event.preventDefault();
  const input = dom.loginForgotEmail();
  const email = input ? input.value.trim() : '';
  if (!email) {
    setFieldError(dom.loginForgotError, 'Enter the email address on your account.');
    return;
  }
  const button = dom.loginForgotSubmit();
  if (button) button.disabled = true;
  try {
    await adminAPI.fetch(cfg.apiRoutes.forgotPassword, {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipUnauthorizedHandler: true,
    });
    // Straight on to the redemption panel. Nothing here reveals whether an
    // email was actually sent — the server does not say, and neither do we.
    openResetPanel('', 'If that address belongs to an account, a code is on its way. '
      + 'Paste it below with your new password. It expires shortly.');
  } catch (err) {
    // A cool-off or a blocked address answers with a message written for the
    // person reading it, so show that rather than a generic line.
    setFieldError(dom.loginForgotError, errorText(err, 'Could not send a reset code.'));
  } finally {
    if (button) button.disabled = false;
  }
}

async function handleResetSubmit(event) {
  if (event) event.preventDefault();
  const tokenInput = dom.loginResetToken();
  const next = dom.loginResetNew();
  const confirm = dom.loginResetConfirm();
  const mfaField = dom.loginResetMfaField();
  const mfaInput = dom.loginResetMfaCode();

  const token = tokenInput ? tokenInput.value.trim() : '';
  const nextValue = next ? next.value : '';
  if (!token) {
    setFieldError(dom.loginResetError, 'Paste the code from the email.');
    return;
  }
  if (!nextValue) {
    setFieldError(dom.loginResetError, 'Choose a new password.');
    return;
  }
  if (nextValue !== (confirm ? confirm.value : '')) {
    setFieldError(dom.loginResetError, 'The new passwords do not match.');
    return;
  }
  const body = { token, new_password: nextValue };
  const mfaCode = mfaInput && !(mfaField && mfaField.hidden) ? mfaInput.value.trim() : '';
  if (mfaCode) body.mfa_code = mfaCode;

  const button = dom.loginResetSubmit();
  if (button) button.disabled = true;
  setFieldError(dom.loginResetError, '');
  try {
    await adminAPI.fetch(cfg.apiRoutes.resetPassword, {
      method: 'POST',
      body: JSON.stringify(body),
      skipUnauthorizedHandler: true,
    });
    backToCredentials();
    adminNotifications.showFlash('Password updated — sign in with your new password');
    // No session comes back from a reset: holding the mailbox is not holding
    // the account. Signing in is also the first confirmation that the
    // password is the one they meant.
    const emailField = document.querySelector('#loginForm input[name="email"]');
    if (emailField) setTimeout(() => { try { emailField.focus(); } catch {} }, 60);
  } catch (err) {
    const code = errorCode(err);
    if (code === 'mfa_required') {
      // Privileged role: the token alone is not enough. Reveal the field and
      // keep everything else as typed — the token is still unspent.
      if (mfaField) mfaField.hidden = false;
      setFieldError(dom.loginResetError, errorText(err, 'Your role requires a second factor.'));
      if (mfaInput) setTimeout(() => { try { mfaInput.focus(); } catch {} }, 60);
      return;
    }
    if (code === 'mfa_invalid') {
      if (mfaField) mfaField.hidden = false;
      if (mfaInput) mfaInput.value = '';
      setFieldError(dom.loginResetError, errorText(err, 'That code was not accepted.'));
      return;
    }
    setFieldError(dom.loginResetError, errorText(err, 'Could not set that password.'));
  } finally {
    if (button) button.disabled = false;
  }
}

/**
 * Pick up `?reset_token=` from an emailed link and open the reset panel on it.
 *
 * The token is stripped from the address bar immediately: it is a bearer
 * credential, and leaving it in the URL puts it in the browser history, in
 * any `Referer` the page later sends, and on the screen of whoever is
 * standing behind the user.
 */
export function consumeResetTokenFromUrl() {
  let token = '';
  try {
    const params = new URLSearchParams(window.location.search || '');
    token = (params.get('reset_token') || '').trim();
    if (!token) return false;
    params.delete('reset_token');
    const rest = params.toString();
    window.history.replaceState(
      {}, '', window.location.pathname + (rest ? `?${rest}` : '') + (window.location.hash || ''),
    );
  } catch {
    if (!token) return false;
  }
  const overlay = dom.loginOverlay();
  if (overlay) {
    overlay.hidden = false;
    overlay.style.display = '';
  }
  openResetPanel(token, 'Choose a new password for your account. This link works once.');
  return true;
}

// ----- Password-expiry banner -----------------------------------------

export function showPasswordExpiryBanner(daysLeft) {
  const banner = dom.passwordExpiryBanner();
  const text = dom.passwordExpiryText();
  if (!banner || !text || daysLeft == null || daysLeft < 0) return;
  const when = daysLeft === 0
    ? 'today'
    : `in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`;
  text.textContent = `Your password expires ${when}. Change it now and you won't be asked at sign-in.`;
  banner.hidden = false;
}

function hidePasswordExpiryBanner() {
  const banner = dom.passwordExpiryBanner();
  if (banner) banner.hidden = true;
}

// ----- Logout ---------------------------------------------------------

export function performLogout({ skipShiftGuard = false, reason = 'user_initiated' } = {}) {
  const sm = window.adminViews && window.adminViews.shiftManagement;
  const currentShiftInfo = sm && typeof sm.getCurrentShift === 'function' ? sm.getCurrentShift() : null;
  if (!skipShiftGuard
      && hasRole('customer_care')
      && currentShiftInfo && currentShiftInfo.login_at && !currentShiftInfo.logout_at) {
    closeProfileMenu();
    adminNotifications.showFlash('Close your shift before logging out.', 'error');
    if (sm && typeof sm.openCloseDialog === 'function') sm.openCloseDialog({ logoutAfter: true }).catch(() => {});
    return;
  }
  closeProfileMenu();
  const ds = window.adminViews && window.adminViews.dailyStatus;
  if (ds && ds.state && ds.state.timer) {
    clearInterval(ds.state.timer);
    ds.state.timer = null;
  }
  if (sm) {
    if (typeof sm.stopPolling === 'function') sm.stopPolling();
    if (typeof sm.clearEndAlert === 'function') sm.clearEndAlert({ resetSchedule: true });
  }
  try { adminAuth.clearToken(); } catch {}
  const iframe = cfg.getAnalyticsIframe();
  if (iframe) iframe.setAttribute('src', 'about:blank');
  const viewElements = cfg.getViewElements() || {};
  Object.values(viewElements).forEach((el) => { if (el) el.style.display = 'none'; });
  document.querySelectorAll('.nav .nav-view').forEach((btn) => btn.classList.remove('active'));
  const overlay = dom.loginOverlay();
  if (overlay) {
    overlay.hidden = false;
    overlay.style.display = 'grid';
  }
  if (reason === 'shift_end_timeout') {
    adminNotifications.showFlash('Shift ended. You have been logged out.', 'info', 8000);
  }
}

// ----- Listener wiring ------------------------------------------------

let listenersInstalled = false;

/**
 * Wire up DOM event listeners. Idempotent — safe to call multiple
 * times. admin.js calls this once after `configureAuth` and after the
 * DOM elements are reachable.
 */
export function installAuthListeners() {
  if (listenersInstalled) return;
  listenersInstalled = true;

  // Login form
  const loginForm = dom.loginForm();
  if (loginForm) {
    const loginSubmitBtn = dom.loginSubmitBtn();
    if (cfg.apiRoutes.login) loginForm.setAttribute('action', cfg.apiRoutes.login);
    loginForm.noValidate = true;
    loginForm.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      handleLoginSubmit(event);
    });
    loginForm.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleLoginSubmit(event);
      }
    });
    if (loginSubmitBtn) {
      loginSubmitBtn.addEventListener('click', handleLoginSubmit);
    }
  }

  // Profile menu (`<details>` element)
  const profileMenu = dom.profileMenu();
  const profileMenuToggle = dom.profileMenuToggle();
  if (profileMenu) {
    profileMenu.addEventListener('toggle', () => {
      if (profileMenuToggle) {
        profileMenuToggle.setAttribute('aria-expanded', profileMenu.open ? 'true' : 'false');
      }
    });
    document.addEventListener('click', (event) => {
      if (!profileMenu.open) return;
      if (profileMenu.contains(event.target)) return;
      profileMenu.open = false;
    });
  }
  const profileMenuHelp = dom.profileMenuHelp();
  if (profileMenuHelp) {
    profileMenuHelp.addEventListener('click', () => {
      closeProfileMenu();
    });
  }
  const profileMenuProfileBtn = dom.profileMenuProfileBtn();
  if (profileMenuProfileBtn) {
    profileMenuProfileBtn.addEventListener('click', () => {
      openProfileDialog();
    });
  }

  // Sign-in sequence panels
  const mfaForm = dom.loginMfaForm();
  if (mfaForm) mfaForm.addEventListener('submit', handleLoginMfaSubmit);
  const mfaCancel = dom.loginMfaCancel();
  if (mfaCancel) {
    mfaCancel.addEventListener('click', () => {
      clearLoginFlow();
      setFieldError(dom.loginMfaError, '');
      showLoginStep('credentials');
    });
  }
  const changeForm = dom.loginChangeForm();
  if (changeForm) changeForm.addEventListener('submit', handleForcedChangeSubmit);
  const forgotLink = dom.loginForgotLink();
  if (forgotLink) forgotLink.addEventListener('click', () => openForgotPanel());
  const forgotForm = dom.loginForgotForm();
  if (forgotForm) forgotForm.addEventListener('submit', handleForgotSubmit);
  const forgotCancel = dom.loginForgotCancel();
  if (forgotCancel) forgotCancel.addEventListener('click', backToCredentials);
  const resetForm = dom.loginResetForm();
  if (resetForm) resetForm.addEventListener('submit', handleResetSubmit);
  const resetCancel = dom.loginResetCancel();
  if (resetCancel) resetCancel.addEventListener('click', backToCredentials);
  const enrolForm = dom.loginEnrolForm();
  if (enrolForm) enrolForm.addEventListener('submit', handleEnrolSubmit);
  const enrolCopy = dom.loginEnrolCopy();
  if (enrolCopy) {
    enrolCopy.addEventListener('click', () => {
      const secret = loginFlow.enrolSecret;
      if (!secret) return;
      try {
        navigator.clipboard.writeText(secret);
        adminNotifications.showFlash('Setup key copied');
      } catch {
        adminNotifications.showFlash('Could not copy — select the key and copy it manually.', 'error');
      }
    });
  }

  // Password-expiry banner
  const expiryAction = dom.passwordExpiryAction();
  if (expiryAction) expiryAction.addEventListener('click', () => openProfileDialog());
  const expiryDismiss = dom.passwordExpiryDismiss();
  if (expiryDismiss) expiryDismiss.addEventListener('click', hidePasswordExpiryBanner);

  // Profile security panel
  const mfaEnrolBtn = dom.profileMfaEnrolBtn();
  if (mfaEnrolBtn) mfaEnrolBtn.addEventListener('click', startProfileEnrolment);
  const mfaEnrolConfirm = dom.profileMfaEnrolConfirm();
  if (mfaEnrolConfirm) mfaEnrolConfirm.addEventListener('click', confirmProfileEnrolment);
  const mfaEnrolCancel = dom.profileMfaEnrolCancel();
  if (mfaEnrolCancel) {
    mfaEnrolCancel.addEventListener('click', () => {
      const box = dom.profileMfaEnrolBox();
      if (box) box.hidden = true;
    });
  }
  const mfaCodesBtn = dom.profileMfaCodesBtn();
  if (mfaCodesBtn) mfaCodesBtn.addEventListener('click', regenerateRecoveryCodes);
  const mfaDisableBtn = dom.profileMfaDisableBtn();
  if (mfaDisableBtn) mfaDisableBtn.addEventListener('click', disableMfa);
  const sessionsRevokeBtn = dom.profileSessionsRevokeBtn();
  if (sessionsRevokeBtn) sessionsRevokeBtn.addEventListener('click', revokeOtherSessions);

  // Profile / password dialog
  const profileDialog = dom.profileDialog();
  if (profileDialog) {
    profileDialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      const form = dom.profileForm();
      if (form) form.reset();
      clearProfileFormError();
      ensureProfileDialogClosed();
    });
    profileDialog.addEventListener('close', () => {
      const form = dom.profileForm();
      if (form) form.reset();
      clearProfileFormError();
    });
  }
  const profileForm = dom.profileForm();
  if (profileForm) {
    profileForm.addEventListener('submit', handleProfileFormSubmit);
  }

  // Top-bar logout button (separate from the profile-menu logout)
  const logoutBtn = dom.logoutBtn();
  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => {
      performLogout();
    });
  }
}
